/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

/**
 * Loads Google SDK.
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiclientloadname--------version--------callback
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiauth2initparams
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiauth2getauthinstance
 */
export const loadSocial = (appId: string): Promise<void> => new Promise((resolve, reject) => {
    const firstJS = document.getElementsByTagName('script')[0];
    const js = document.createElement('script');

    js.src = '//apis.google.com/js/platform.js';
    js.id = 'gapi-client';
    js.onload = () => {
        window.gapi.load('auth2', () => {
            if (!window.gapi.auth2.getAuthInstance()) {
                window.gapi.auth2.init({
                    client_id: appId,
                    fetchBasicProfile: true,
                    scope: null
                }).then(() => resolve(), (err: any) => reject({
                    provider: 'google',
                    type: 'load',
                    description: 'Failed to load SDK',
                    error: err
                }));
            } else resolve();
        });
    };

    if (!firstJS?.parentNode) document.appendChild(js);
    else firstJS.parentNode.appendChild(js);
});

/**
 * Checks if user is logged in to app through Google.
 * Requires SDK to be loaded first.
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiauth2getauthinstance
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#googleauthissignedinget
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#googleauthcurrentuserget
 */
export const checkLogin = () => new Promise((resolve, reject) => {
    const GoogleAuth = window.gapi.auth2.getAuthInstance();

    if (!GoogleAuth.isSignedIn.get()) {
        return reject({
            provider: 'google',
            type: 'check_login',
            description: 'Not authenticated',
            error: null
        });
    }

    return resolve(GoogleAuth.currentUser.get());
});

/**
 * Trigger Google login process.
 * Requires SDK to be loaded first.
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiauth2getauthinstance
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#googleauthsignin
 */
export const login = () => new Promise((resolve, reject) => {
    const GoogleAuth = window.gapi.auth2.getAuthInstance();

    GoogleAuth.signIn().then(
        () => checkLogin().then(resolve, reject),
        (err: any) => reject({
            provider: 'google',
            type: 'auth',
            description: 'Authentication failed',
            error: err
        })
    );
});

/**
 * Trigger Google logout.
 * Requires SDK to be loaded first.
 * @see https://developers.google.com/api-client-library/javascript/reference/referencedocs#googleauthsignout
 */
export const googleLogout = () => new Promise((resolve, reject) => {
    const GoogleAuth = window.gapi.auth2.getAuthInstance();
    GoogleAuth.signOut().then(resolve, reject);
})